import { useMemo } from 'react';
import {
  Table,
  TableHeader,
} from '@fortress-technology-solutions/fortress-component-library/Organisms_Fortress';
import TableLayout from '../../../components/TableLayoutV2';
import { useNewTenantEventsTable } from './hooks';
import SubmitToTracsModal from '../SubmitToTracsModal';
import messages from './messages';
import parentMessages from '../messages';
import { Banner } from '@fortress-technology-solutions/fortress-component-library/Atoms';
import { BASE_TABLE_LAYOUT_SX } from '../constants';

function NewTenantEvents({
  show,
  tenantEvents = [],
  tenantEventsLoading = false,
  voucherSubmissionStatus,
  onMoveToNextVoucher,
  isMovingToNextVoucher = false,
  onDownloadCertification,
  isDownloadingCertification = false,
  onSubmitToTracs,
  isReadOnly = false,
  canManage,
  canSubmit,
}) {
  const {
    baseTableProps,
    name,
    allColumnsHidden,
    columnOptions,
    filteredHeaders,
    selectedColumns,
    handleColumnChange,
    filterState,
    filterTypeState,
    searchState,
    dateState,
    handleSortChange,
    handleFilterChange,
    handleFilterTypeChange,
    handleSearchSubmit,
    handleDateSubmit,
    isLoading,
    order,
    orderBy,
    rows,
    count,
    totalCount,
  } = useNewTenantEventsTable({
    tenantEvents,
    isLoading: tenantEventsLoading,
    voucherSubmissionStatus,
    onMoveToNextVoucher: isReadOnly ? undefined : onMoveToNextVoucher,
    isMovingToNextVoucher,
    onDownloadCertification,
    isDownloadingCertification,
    canManage,
  });
  const { intl } = baseTableProps;

  const isCertCompleted = (evt) => evt.certComplete === 'Yes';
  const isSubmittedToTracs = (evt) =>
    ['Submitted', 'Approved'].includes(evt.tracsStatus);
  const isNotSubmittedToTracs = (evt) => evt.tracsStatus === 'Not Submitted';

  const hasEligibleEvents = useMemo(
    () =>
      tenantEvents.some(
        (evt) => isNotSubmittedToTracs(evt) && isCertCompleted(evt),
      ),
    [tenantEvents],
  );

  const submitToTracsButtonTooltip = useMemo(() => {
    if (!canSubmit) {
      return intl.formatMessage(parentMessages.noPermissionTooltip);
    }
    const allEventsSubmitted = tenantEvents.every(isSubmittedToTracs);
    const hasOpenCertNotSubmitted = tenantEvents.some(
      (evt) => !isCertCompleted(evt),
    );

    if (allEventsSubmitted && tenantEvents.length > 0) {
      return intl.formatMessage(messages.modalButtonTooltipAllEventsSubmitted);
    }
    if (hasOpenCertNotSubmitted || tenantEvents.length === 0) {
      return intl.formatMessage(messages.modalButtonTooltipNoEvents);
    }
  }, [tenantEvents, intl, canSubmit]);

  if (!show) {
    return null;
  }

  return (
    <TableLayout name={name} fullPage sx={BASE_TABLE_LAYOUT_SX}>
      {(height) => (
        <>
          <TableHeader
            title={intl.formatMessage(messages.title)}
            count={count}
            totalCount={totalCount}
            legend={[
              {
                color: 'error.lighter',
                label: intl.formatMessage(messages.needsAttention),
              },
            ]}
            actions={
              <SubmitToTracsModal
                onSend={onSubmitToTracs}
                disabled={
                  isReadOnly ||
                  !canSubmit ||
                  !hasEligibleEvents ||
                  !onSubmitToTracs
                }
                confirmLabel={intl.formatMessage(messages.confirmLabel)}
                buttonText={intl.formatMessage(messages.modalButtonText)}
                buttonTooltip={submitToTracsButtonTooltip}
                title={intl.formatMessage(messages.modalTitle)}
                description={intl.formatMessage(messages.modalDescription)}
                submitButtonText={intl.formatMessage(messages.sendReceive)}
              />
            }
          >
            <Banner
              status="info"
              message={intl.formatMessage(messages.bannerMessage)}
            />
          </TableHeader>
          <Table
            {...baseTableProps}
            name={name}
            allColumnsHidden={allColumnsHidden}
            columnOptions={columnOptions}
            dateState={dateState}
            selectedColumns={selectedColumns}
            handleColumnChange={handleColumnChange}
            filterState={filterState}
            filterTypeState={filterTypeState}
            handleSortChange={handleSortChange}
            handleFilterChange={handleFilterChange}
            handleFilterTypeChange={handleFilterTypeChange}
            handleSearchSubmit={handleSearchSubmit}
            handleDateSubmit={handleDateSubmit}
            headers={filteredHeaders}
            isLoading={isLoading}
            order={order}
            orderBy={orderBy}
            rows={rows}
            searchState={searchState}
            count={count}
            height={height}
            emptyStateMessage="No tenant events found"
            totalCount={totalCount}
          />
        </>
      )}
    </TableLayout>
  );
}

export default NewTenantEvents;
